import { useCallback, useMemo } from 'react';

import type {
  CsrPlayerElement,
  MetricEvent,
} from '@spotify-internal/csr-player';
import { MetricEventName } from '@spotify-internal/csr-player';

import type { EventMarkerData } from './EventMarker';

export const getRouteLabel = (event: MetricEvent): string | undefined =>
  event.name === MetricEventName.RouteChange ? event.data.to : undefined;

type UseEventMarkersOptions = {
  getColor: (event: MetricEvent) => string;
  getLabel?: (event: MetricEvent) => string | undefined;
  onMarkerClick?: (timestampSeconds: number) => void;
  onSeek: (_: unknown, value: number | number[]) => void;
};

type UseEventMarkersResult = {
  markers: EventMarkerData[];
  handleMarkerClick: (marker: MetricEvent) => void;
};

export const useEventMarkers = (
  metricEvents: MetricEvent[],
  player: CsrPlayerElement,
  options: UseEventMarkersOptions,
): UseEventMarkersResult => {
  const { getColor, getLabel, onMarkerClick, onSeek } = options;

  const markers = useMemo<EventMarkerData[]>(() => {
    if (!player.duration) return [];
    return metricEvents.map(event => ({
      ...event,
      position: (event.timestamp / player.duration) * 100,
      color: getColor(event),
      label: getLabel?.(event),
    }));
  }, [metricEvents, player.duration, getColor, getLabel]);

  const handleMarkerClick = useCallback(
    (marker: MetricEvent) => {
      onSeek(null, marker.timestamp * 1000);
      player.pause();
      onMarkerClick?.(marker.timestamp);
    },
    [player, onSeek, onMarkerClick],
  );

  return { markers, handleMarkerClick };
};
